import { Typography } from "@mui/material";
import { hasAnyAccessToResource } from "api/general";
import { useReward } from "api/rewards";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { ReactComponent as Settings01Icon } from "components/Icons/settings-01.svg";
import { ImportJsonModal } from "components/ImportJsonModal/ImportJsonModal";
import { ImportCsvModal } from "components/Layout/ImportCsv/ImportCsvModal/ImportCsvModal";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { useTableSelectable } from "components/Table/useTableSelectable";
import { TargetTenantModal } from "components/TargetTenantModal/TargetTenantModal";
import AddCoupon from "components/Tooltips/AddCoupon";
import ClaimRewardModal, {
  ClaimRewardData,
} from "components/Tooltips/ClaimRewardModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useIsOverflown } from "hooks/useIsOverflown";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { setImportedRewards } from "reduxState/rewards/rewards";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { REWARD_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { Nullable } from "types/global";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { exportToJsonFile } from "utilities/exportToJson";
import { links } from "utilities/links";
import { isEmpty } from "utilities/objects";
import { AvailabilityColumn } from "./AvailabilityColumn/AvailabilityColumn";
import { prepareExportRewardsJson } from "./helpers";
import { ManageImages } from "./ManageImages";
import { useStyles } from "./RewardList.styles";

export enum Modals {
  AddCoupon = "addCoupon",
  ClaimReward = "claimReward",
  ManageImages = "manageImages",
  ImportRewards = "importRewards",
  DifferentTenant = "differentTenant",
  ImportCoupons = "importCoupons",
}

export function RewardList() {
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { listing, updateListing } = useListing(REWARD_LIST_ID);
  const appDispatch = useAppDispatch();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const {
    rowsSelected,
    selectRow,
    unselectRow,
    selectAll,
    unselectAll,
    disabled,
    clearSelection,
  } = useTableSelectable();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[REWARD_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      REWARD_LIST_ID,
    );

    setFilters(filters);
  };
  const { observeElement, isOverflown } = useIsOverflown();
  const hasAnyLanguagePermission = hasAnyAccessToResource("LANGUAGE");
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const rewardsExportData = prepareExportRewardsJson(
    rowsSelected.map((row) => row.data),
  );

  const { useRewardList, importCoupons } = useReward();
  const { data, error } = useRewardList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "status",
      isSortable: false,
    },
    {
      headerText: message({ id: "type" }),
      property: "reward",
      isSortable: true,
    },
    {
      headerText: message({ id: "availability" }),
      property: "availability",
      isSortable: false,
      align: "right",
      tooltip: (
        <>
          <Typography className={classes.tooltipTitle}>
            {message({ id: "availability" })}
          </Typography>
          <Typography className={classes.tooltipDescription}>
            {message({ id: "availabilityInfo" })}
          </Typography>
        </>
      ),
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
  ]);

  function createItems(items: any[]): Row[] {
    return items.map((item) => ({
      cells: [
        {
          content: item.name,
          property: "name",
        },
        {
          content: <StatusValue status={item.active} />,
          property: "status",
        },
        {
          content: message({
            id:
              item.couponValueType === "percentage"
                ? "percentageCoupon"
                : item.reward,
          }),
          property: "reward",
        },
        {
          align: "right",
          content: (
            <AvailabilityColumn
              usageLeft={item?.usageLeft}
              usageLimitGeneral={item?.usageLimit?.general}
              active={item?.active}
            />
          ),
          property: "availability",
        },
        { content: OLDate(item.createdAt), property: "createdAt" },
      ],
      id: item.rewardId,
      metadata: {
        disabled: !item.active,
        rewardType: item.reward,
        showCoupons:
          ["static_coupon", "dynamic_coupon", "conversion_coupon"].includes(
            item.reward,
          ) && isEmpty(item.couponGenerator),
      },
      data: item,
    }));
  }

  const items = data?.items ? createItems(data.items) : [];

  const [openedItem, setOpenedItem] = useState<{
    id: string;
    type?: string;
    dateValid?: Nullable<string>;
  }>({ id: "", type: "", dateValid: undefined });
  const [openedModalType, setOpenedModalType] =
    useState<Nullable<Modals>>(null);

  const [isSelectable, setIsSelectable] = useState(false);

  const closeModal = () => {
    setOpenedItem({ id: "", type: "", dateValid: undefined });
    setOpenedModalType(null);
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, REWARD_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);

  return (
    <>
      <TopHeader title={message({ id: "rewards" })} />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "rewards" })}
          setFilters={setSelectedFilters}
          total={data?.total}
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "categories",
              label: message({ id: "categories" }),
              type: "reward-categories",
            },
            {
              query: "labels",
              label: message({ id: "customAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "costInPoints",
              label: message(
                { id: "priceInUnits" },
                { units: defaultWallet?.unitPluralName },
              ),
              type: "number",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "reward-name-filter",
            },
            {
              query: "reward",
              label: message({ id: "type" }),
              type: "reward-types-filter",
            },
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "featured",
              label: message({ id: "featured" }),
              type: "boolean-status",
            },
            {
              query: "price",
              label: message({ id: "price" }),
              type: "number",
            },
            {
              query: "public",
              label: message({ id: "public" }),
              type: "boolean-status",
            },

            {
              query: "segments",
              label: message({ id: "segments" }),
              type: "segments",
            },
            {
              query: "levels",
              label: message({ id: "tiers" }),
              type: "tiers",
            },
            {
              query: "usageLimit:perUser",
              label: message({ id: "redemptionLimitPerMember" }),
              type: "number",
            },
          ]}
        />
        <SubheaderWrapper>
          <SubheaderLinkButton
            text={message({ id: "configuration" })}
            link={links.rewardsConfiguration}
            icon={<Settings01Icon />}
            variant="outlined"
            data-test-id="rewards-list-configuration-button"
          />
          <SubheaderMenuButton
            text={message({ id: "addReward" })}
            menuItems={[
              {
                onClick: () => navigate(links.addReward),
                text: message({ id: "addReward" }),
              },
              {
                text: message({ id: "importRewards" }),
                onClick: () => setOpenedModalType(Modals.ImportRewards),
              },
            ]}
            disabled={!hasAnyLanguagePermission}
            data-test-id="rewards-add-reward-button"
          />
        </SubheaderWrapper>
      </Subheader>
      <Subheader className={classes.horizontalCenter}>
        {isSelectable ? (
          <Tooltip
            text={message({ id: "exportImagesWarning" })}
            disableHoverListener={!isOverflown}
            placement="bottom-start"
          >
            <span className={classes.warningContainer}>
              <AlertTriangleIcon className={classes.warnIcon} />
              <Typography className={classes.warningText} ref={observeElement}>
                {message({ id: "exportImagesWarning" })}
              </Typography>
            </span>
          </Tooltip>
        ) : null}
        <SubheaderWrapper alignRight>
          {isSelectable ? (
            <>
              <SubheaderActionButton
                text={message({ id: "close" }).toUpperCase()}
                onClick={() => setIsSelectable(false)}
              />
              <SubheaderMenuButton
                text={`${message({ id: "bulkActions" }).toUpperCase()}... (${rowsSelected.length})`}
                menuItems={[
                  {
                    text: message({ id: "downloadJsonFile" }),
                    onClick: async () => {
                      exportToJsonFile({
                        data: prepareExportRewardsJson(
                          rowsSelected.map((row) => row.data),
                        ),
                        name: "rewards",
                        callback: () => {
                          clearSelection();
                          return setIsSelectable(false);
                        },
                      });
                    },
                  },
                  {
                    text: message({ id: "copyToDifferentTenant" }),
                    onClick: () => setOpenedModalType(Modals.DifferentTenant),
                  },
                ]}
                hideArrow
                disabled={rowsSelected.length === 0}
                data-test-id="table-actions"
              />
            </>
          ) : (
            <SubheaderActionButton
              text={message({ id: "bulkActions" }).toUpperCase()}
              onClick={() => setIsSelectable(true)}
              data-test-id="select-to-export"
            />
          )}
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            className={classes.dynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: REWARD_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={REWARD_LIST_ID}
        data-test-id="rewards"
        onRowClick={(id: string) => {
          navigate(links.reward(id as string));
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              label: message({ id: "addCoupon" }),
              action: (
                item?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setOpenedItem({
                  id: item as string,
                  type: data?.items?.find(
                    (reward: any) => reward.rewardId === (item as string),
                  )?.reward,
                });
                setOpenedModalType(Modals.AddCoupon);
              },
              hideCondition: (item: Row) => !item.metadata?.showCoupons,
            },
            {
              label: message({ id: "claimFor" }),
              action: (
                item?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setOpenedItem({
                  id: item as string,
                  type: data?.items?.find(
                    (reward: any) => reward.rewardId === (item as string),
                  )?.reward,
                  dateValid: data?.items?.find(
                    (reward: any) => reward.rewardId === (item as string),
                  )?.dateValid,
                });
                setOpenedModalType(Modals.ClaimReward);
              },
            },
            {
              label: message({ id: "edit" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editReward(id as string)),
              disableCondition: () => !hasAnyLanguagePermission,
            },
            {
              label: message({ id: "importCoupons" }),
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setOpenedItem({ id: id as string });
                setOpenedModalType(Modals.ImportCoupons);
              },
              hideCondition: (item: Row) => !item.metadata?.showCoupons,
            },
            {
              action: (id) =>
                navigate(links.addReward, {
                  state: {
                    rewardToDuplicate: id,
                  },
                }),
              label: message({ id: "duplicateReward" }),
            },
            {
              label: message({ id: "manageImages" }),
              action: (
                item?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setOpenedItem({
                  id: item as string,
                });
                setOpenedModalType(Modals.ManageImages);
              },
            },
          ],
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        selectable={
          isSelectable
            ? {
                rowsSelected,
                selectRow,
                unselectRow,
                selectAll,
                unselectAll,
                disabled,
                clearSelection,
              }
            : undefined
        }
        clearFilters={() => setFilters([])}
      />
      <ClaimRewardModal
        closeModal={closeModal}
        open={openedModalType === Modals.ClaimReward}
        reward={openedItem as ClaimRewardData}
      />
      <ManageImages
        closeModal={closeModal}
        isOpen={openedModalType === Modals.ManageImages}
        data={openedItem}
      />
      {openedModalType && openedModalType === "addCoupon" && (
        <AddCoupon
          closeModal={closeModal}
          open={openedModalType === Modals.AddCoupon}
          rewardCode={openedItem.id}
        />
      )}
      <ImportCsvModal
        headerMessage="importCoupons"
        closeModal={closeModal}
        isModalOpen={openedModalType === Modals.ImportCoupons}
        importData={importCoupons}
        importDataId={openedItem.id}
      />
      <ImportJsonModal
        headerMessage="importRewards"
        isOpen={openedModalType === Modals.ImportRewards}
        onClose={() => setOpenedModalType(null)}
        importTooBigErrorMessage="maxImportRewardCountReached"
        setJsonAction={setImportedRewards}
        link={links.rewardsImportList}
      />
      <TargetTenantModal
        isOpen={openedModalType === Modals.DifferentTenant}
        headerText={message(
          {
            id:
              rowsSelected.length === 1
                ? "copyOneRewardToTargetTenant"
                : "copyRewardsToTargetTenant",
          },
          {
            count: rowsSelected.length,
          },
        )}
        onClose={() => setOpenedModalType(null)}
        setJsonAction={setImportedRewards}
        data={rewardsExportData}
        link={links.rewardsImportList}
        buttonText={message({
          id:
            rowsSelected.length === 1
              ? "copyRewardAndViewTargetTenant"
              : "copyRewardsAndViewTargetTenant",
        })}
        infoText={message({ id: "copiedRewardsInfo" })}
      />
    </>
  );
}
